import SlideOver from "@components/ui/SlideOver";
import Spinner from "@components/ui/Spinner";
import Button from "@components/ui/Button";
import Input from "@components/ui/Input";
import Textarea from "@components/ui/Textarea";

import {
  CLINIC_COUNTRIES,
  DEFAULT_CLINIC_COUNTRY,
} from "@utils/clinicFields";

import OpeningHoursEditor from "./OpeningHoursEditor";
import ProvidersEditor from "./ProvidersEditor";
import UnifiEditor from "./UnifiEditor";
import OutboundEditor from "./OutboundEditor";
import SchedulingEditor from "./SchedulingEditor";
import SikkaEditor from "./SikkaEditor";
import AgentQuestionsEditor from "@components/admin-clinics/AgentQuestionsEditor";
import CallFlowsEditor from "@components/admin-clinics/CallFlowsEditor";


export default function EditClinicDrawer({
  open,
  onClose,

  selectedClinicId,
  selectedClinic,
  draft,
  onChangeField,

  sikkaPractices,
  sikkaPracticesLoading,
  onLoadSikkaPractices,
  onConnectSikka,
  sikkaConnecting,

  purposes,
  questionLibrary,
  questionLibraryLoading,

  agentQuestionsByPurpose,
  setAgentQuestionsByPurpose,

  dirtyClinic,
  dirtyAgentQuestions,
  saving,
  onSave,

  isValidAgentQuestions = true,

}) {
  const canSave =
    (dirtyClinic || dirtyAgentQuestions) && !saving && isValidAgentQuestions;

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Edit Clinic"
      footer={
        <div className="flex gap-2 justify-end">
          <Button color="light" onClick={onClose}>
            Cancel
          </Button>
          <Button color="dark" disabled={!canSave} onClick={onSave}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      }
    >
      {!draft ? (
        <div className="text-center py-4">
          <Spinner size="sm" />
          <span className="ml-2">Loading clinic data...</span>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="border rounded-lg p-4 bg-white space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Clinic Details</div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={draft.is_active !== false}
                  onChange={(e) => onChangeField("is_active", e.target.checked)}
                />
                <span>Active</span>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Clinic Name"
                value={draft.clinic_name ?? ""}
                onChange={(e) => onChangeField("clinic_name", e.target.value)}
              />

              <Input
                label="Twilio Phone (AI agent)"
                value={draft.phone ?? ""}
                onChange={(e) => onChangeField("phone", e.target.value)}
              />

              <Input
                label="Clinic Phone (original)"
                value={draft.clinic_phone ?? ""}
                onChange={(e) => onChangeField("clinic_phone", e.target.value)}
              />

              <Input
                label="Timezone"
                value={draft.preferred_timezone ?? ""}
                onChange={(e) =>
                  onChangeField("preferred_timezone", e.target.value)
                }
              />

              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <select
                  value={draft.country ?? DEFAULT_CLINIC_COUNTRY}
                  onChange={(e) => onChangeField("country", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition bg-white"
                >
                  {CLINIC_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label} ({c.dial})
                    </option>
                  ))}
                </select>
                <div className="text-xs text-gray-500 mt-1">
                  Patient numbers from the PMS have no country code. This is the
                  code added before dialling.
                </div>
              </div>

              <Input
                label="Agent Name"
                value={draft.clinic_agent_name ?? ""}
                onChange={(e) =>
                  onChangeField("clinic_agent_name", e.target.value)
                }
              />

              <Input
                label="Appointment Duration (minutes)"
                type="number"
                min="5"
                value={draft.appointment_duration_minutes ?? 60}
                onChange={(e) =>
                  onChangeField(
                    "appointment_duration_minutes",
                    e.target.value === "" ? "" : parseInt(e.target.value, 10),
                  )
                }
              />
            </div>

            <Input
              label="Address"
              value={draft.address ?? ""}
              onChange={(e) => onChangeField("address", e.target.value)}
            />

            <Input
              label="ElevenLabs Agent ID (Inbound)"
              placeholder="e.g., abc123xyz..."
              value={draft.elevenlabs_agent_id ?? ""}
              onChange={(e) =>
                onChangeField("elevenlabs_agent_id", e.target.value)
              }
            />

            <Input
              label="ElevenLabs Outbound Agent ID (Optional)"
              placeholder="Leave empty to use inbound agent"
              value={draft.elevenlabs_outbound_agent_id ?? ""}
              onChange={(e) =>
                onChangeField("elevenlabs_outbound_agent_id", e.target.value)
              }
            />

            <Input
              label="ElevenLabs Phone Number ID"
              placeholder="e.g., pn_abc123..."
              value={draft.elevenlabs_phone_number_id ?? ""}
              onChange={(e) =>
                onChangeField("elevenlabs_phone_number_id", e.target.value)
              }
            />

            <label className="flex items-center gap-3 py-2">
              <input
                type="checkbox"
                className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={draft.outbound_calls_enabled ?? false}
                onChange={(e) => onChangeField("outbound_calls_enabled", e.target.checked)}
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Enable Outbound Calls</span>
                <p className="text-xs text-gray-500">Allow initiating confirmation calls to patients from the dashboard</p>
              </div>
            </label>
          </div>

          <OutboundEditor
            enabled={!!draft.outbound_calls_enabled}
            maxConcurrent={draft.outbound_max_concurrent_calls}
            transferEnabled={!!draft.transfer_campaigns_enabled}
            onChangeField={onChangeField}
          />

          <UnifiEditor
            enabled={draft.unifi_scraping_enabled}
            consoleId={draft.unifi_console_id}
            onChange={(field, value) => {
              const key = field === "enabled"
                ? "unifi_scraping_enabled"
                : "unifi_console_id";
              onChangeField(key, value);
            }}
          />

          <SchedulingEditor
            provider={draft.active_pms_provider}
            config={draft.pms_config}
            onChange={onChangeField}
          />

          <SikkaEditor
            provider={selectedClinic?.active_pms_provider}
            config={selectedClinic?.pms_config}
            practices={sikkaPractices}
            practicesLoading={sikkaPracticesLoading}
            onLoadPractices={onLoadSikkaPractices}
            onConnect={onConnectSikka}
            connecting={sikkaConnecting}
          />

          <OpeningHoursEditor
            value={draft.opening_hours}
            onChange={(newHours) => onChangeField("opening_hours", newHours)}
          />

          {selectedClinic?.active_pms_provider && selectedClinicId && (
            // Keyed on the PMS link so a Connect & Sync on an already-linked
            // clinic remounts the editor and it reads the freshly synced list.
            <ProvidersEditor key={selectedClinic?.pms_config?.office_id ?? ""}
                             clinicId={selectedClinicId} />
          )}

          <CallFlowsEditor clinicId={selectedClinicId} />

          <div className="border rounded-lg p-4 bg-white space-y-4">
            <div className="text-sm font-semibold">Messages</div>

            <Textarea
              label="Clinic Info"
              rows={4}
              value={draft.clinic_info ?? ""}
              onChange={(e) => onChangeField("clinic_info", e.target.value)}
            />

            <Textarea
              label="Welcome Message"
              rows={4}
              value={draft.welcome_message ?? ""}
              onChange={(e) => onChangeField("welcome_message", e.target.value)}
            />
          </div>

          <div className="border rounded-lg p-4 bg-white space-y-2">
            <div className="text-sm font-semibold">Agent Questions</div>
            <div className="text-xs text-gray-600">
              These are saved to the new clinic_agent_questions tables.
            </div>

            {questionLibraryLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-600 py-2">
                <Spinner size="sm" />
                <span>Loading question library…</span>
              </div>
            ) : (
              <AgentQuestionsEditor
                purposes={purposes}
                value={agentQuestionsByPurpose}
                onChange={setAgentQuestionsByPurpose}
                questionLibrary={questionLibrary}
              />
            )}

            {!isValidAgentQuestions && (
              <div className="text-sm text-red-600">
                Please fix agent question configuration errors before saving.
              </div>
            )}
          </div>

          <div className="text-xs text-gray-500 break-all">
            Clinic ID: {selectedClinicId}
          </div>
        </div>
      )}
    </SlideOver>
  );
}
